import { useSyncExternalStore } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type MemberSessionUser = {
  id: string
  producerId: string
  name: string
  email: string
  hasPassword: boolean
}

type MemberSessionState = {
  token: string | null
  member: MemberSessionUser | null
  producerName: string | null
  setSession: (token: string, member: MemberSessionUser, producerName: string) => void
  clear: () => void
}

export const useMemberSession = create<MemberSessionState>()(
  persist(
    (set) => ({
      token: null,
      member: null,
      producerName: null,
      setSession: (token, member, producerName) => {
        set({ token, member, producerName })
      },
      clear: () => {
        set({ token: null, member: null, producerName: null })
      },
    }),
    { name: 'vp-member-session' },
  ),
)

export function getMemberToken(): string | null {
  return useMemberSession.getState().token ?? readStoredMemberSession().token
}

export function readStoredMemberSession() {
  if (typeof localStorage === 'undefined') {
    return { token: null as string | null, member: null as MemberSessionUser | null, producerName: null as string | null }
  }
  try {
    const parsed = JSON.parse(localStorage.getItem('vp-member-session') ?? 'null') as {
      state?: {
        token?: string | null
        member?: MemberSessionUser | null
        producerName?: string | null
      }
    } | null
    return {
      token: parsed?.state?.token ?? null,
      member: parsed?.state?.member ?? null,
      producerName: parsed?.state?.producerName ?? null,
    }
  } catch {
    return { token: null, member: null, producerName: null }
  }
}

export function useMemberSessionHydrated() {
  return useSyncExternalStore(
    (onStoreChange) => useMemberSession.persist.onFinishHydration(() => onStoreChange()),
    () => useMemberSession.persist.hasHydrated(),
    () => false,
  )
}
